/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */
// Created/Modified by GitHub Copilot (GPT-5.6 Sol) on 2026-09-02 by ido.kerner@quest.com
// Added direct notification channel list loading for fac-data-table consumers.

import { Injectable } from '@angular/core';
import { FacHttpClientService } from '@foglight/angular-common';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CHANNEL_TYPES,
  NotificationChannel,
} from '../models/notification-management.model';

@Injectable()
export class NotificationChannelsHttpService {
  constructor(private http: FacHttpClientService) {}

  getNotificationChannels(): Observable<NotificationChannel[]> {
    return this.http.get<NotificationChannel[]>('/api/v1/notification-channels').pipe(
      map((notificationChannels: NotificationChannel[]) =>
        notificationChannels.map((notificationChannel) =>
          this.normalizeNotificationChannel(notificationChannel),
        ),
      ),
    );
  }

  getNotificationChannel(id: string): Observable<NotificationChannel> {
    return this.http.get(`/api/v1/notification-channels/${id}`).pipe(
      map((notificationChannel: NotificationChannel) =>
        this.normalizeNotificationChannel(notificationChannel),
      ),
    );
  }

  createNotificationChannel(notificationChannel: NotificationChannel): Observable<NotificationChannel> {
    return this.http.post('/api/v1/notification-channels', notificationChannel);
  }

  updateNotificationChannel(id: string, notificationChannel: NotificationChannel): Observable<NotificationChannel> {
    return this.http.put(`/api/v1/notification-channels/${id}`, notificationChannel);
  }

  deleteNotificationChannel(id: string): Observable<void> {
    return this.http.delete(`/api/v1/notification-channels/${id}`);
  }

  getIntegrationTypes(): Observable<any[]> {
    return this.http.get('/api/v1/notification-channels/integration-types');
  }

  private normalizeNotificationChannel(
    notificationChannel: NotificationChannel,
  ): NotificationChannel {
    if (notificationChannel.type === CHANNEL_TYPES.EMAIL_NOTIFICATION_CHANNEL) {
      return {
        ...notificationChannel,
        type: CHANNEL_TYPES.EMAIL,
        integrationType: 'email',
      };
    }

    if (
      notificationChannel.type === CHANNEL_TYPES.INTEGRATION_NOTIFICATION_CHANNEL
    ) {
      return {
        ...notificationChannel,
        type: CHANNEL_TYPES.INTEGRATION,
      };
    }

    return notificationChannel;
  }
}
